// Scripts/Routing.js - Google Maps Style Navigation
// ==================================================
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Style, Stroke, Icon, Circle as CircleStyle, Fill } from 'ol/style';
import Feature from 'ol/Feature';
import { LineString, Point } from 'ol/geom';
import { toLonLat, fromLonLat } from 'ol/proj';
import { unByKey } from 'ol/Observable';
import { setFloatingControlsVisible, closeSidebarOnMobile } from './CustomFunctions';

// ═══════════════════════════════════════════════════════════════
// STATE
// ═══════════════════════════════════════════════════════════════
let map;
let routeLayer;
let routeSource;
let waypoints = []; // Array of { coords, feature, label, type }
let routeFeature = null;
let isPickingOnMap = false;
let pickingForIndex = -1; // Which waypoint slot we're picking for
let clickListener = null;
let isNavigating = false;
let isFollowingNav = true; // Added follow flag
let navigationWatchId = null;
let currentPositionFeature = null;
let navIdleTimer = null;
const NAV_IDLE_TIME = 5000; // 5 seconds
const RECALC_THROTTLE_TIME = 8000; // 8 seconds
const RECALC_DISTANCE_THRESHOLD = 20; // 20 meters
let lastRecalcCoords = null;
let lastRecalcTime = 0;

// Icons
const iconStart = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="36" width="36" viewBox="0 0 24 24" fill="%2322c55e"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>';
const iconEnd = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="36" width="36" viewBox="0 0 24 24" fill="%23ef4444"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>';
const iconMiddle = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="30" width="30" viewBox="0 0 24 24" fill="%233b82f6"><circle cx="12" cy="12" r="8"/></svg>';

// ═══════════════════════════════════════════════════════════════
// INITIALIZATION
// ═══════════════════════════════════════════════════════════════
function init() {
    try {
        map = $('#map').data('map');
    } catch (e) {
        console.warn('Routing: Map not found yet.');
    }

    if (!map) {
        setTimeout(init, 500);
        return;
    }

    // Routing: Initialized

    // Create route layer
    routeSource = new VectorSource();
    routeLayer = new VectorLayer({
        source: routeSource,
        style: styleFunction,
        zIndex: 9998
    });
    map.addLayer(routeLayer);

    // Listen for manual map movement to stop following
    map.on('movestart', () => {
        if (isNavigating && !map.getView().getAnimating()) {
            isFollowingNav = false;
        }
    });

    // Initialize with Start and End waypoints
    addWaypoint('start');
    addWaypoint('end');

    bindEvents();
    renderWaypointsList();
}

function styleFunction(feature) {
    const type = feature.get('type');
    if (type === 'route') {
        return new Style({
            stroke: new Stroke({
                color: '#c41e3a', // Exact site primary color
                width: 6,
                lineCap: 'round',
                lineJoin: 'round'
            })
        });
    }
    return null; // Markers use their own styles
}

// ═══════════════════════════════════════════════════════════════
// EVENT BINDING
// ═══════════════════════════════════════════════════════════════
function bindEvents() {
    // Toggle routing section
    $(document).on('click', '#btnOpenRouting', function () {
        const section = $('#routingSection');
        $(this).toggleClass('active');
        if (section.is(':visible')) {
            section.slideUp(200);
        } else {
            section.slideDown(200);
        }
    });

    // Start from my location
    $(document).on('click', '#btnStartFromMyLocation', setStartFromMyLocation);

    // Pick on map
    $(document).on('click', '#btnPickOnMap', function () {
        // Find first empty waypoint or toggle picking mode
        const emptyIndex = waypoints.findIndex(w => !w.coords);
        if (emptyIndex !== -1) {
            enableMapPicking(emptyIndex);
        } else {
            // All waypoints filled, pick for end
            enableMapPicking(waypoints.length - 1);
        }
    });

    // Add waypoint
    $(document).on('click', '#btnAddWaypoint', function () {
        // Insert before the last (end) waypoint
        const insertIndex = waypoints.length - 1;
        waypoints.splice(insertIndex, 0, { coords: null, feature: null, label: '', type: 'middle' });
        renderWaypointsList();
        updateGoButtonState();
    });

    // Start navigation
    $(document).on('click', '#btnStartNavigation', startNavigation);

    // End navigation
    $(document).on('click', '#btnEndNavigation', endNavigation);

    // Clear route
    $(document).on('click', '#btnClearRoute', clearAllWaypoints);

    // Waypoint item actions (delegated)
    $(document).on('click', '.waypoint-action-btn.pick', function () {
        const index = $(this).closest('.routing-waypoint-item').data('index');
        enableMapPicking(index);
    });

    // Recenter navigation
    $(document).on('click', '#btnRecenterNav', function () {
        if (window.GeoPST_Geolocation && window.GeoPST_Geolocation.isActive()) {
            const coords = window.GeoPST_Geolocation.getPosition();
            if (coords) {
                isFollowingNav = true; // Resume following
                map.getView().animate({ center: coords, zoom: 11, duration: 600 });
            }
        }
    });

    // Click on GPS blue dot to zoom to current position (from Geolocation Layer)
    map.on('singleclick', (evt) => {
        if (!isNavigating) return;
        const geoLayer = map.getLayers().getArray().find(l => l && l.get('name') === 'GeolocationLayer');
        if (!geoLayer) return;

        const hit = map.forEachFeatureAtPixel(evt.pixel, (f) => f, {
            layerFilter: (l) => l === geoLayer,
            hitTolerance: 6
        });
        if (hit) {
            isFollowingNav = true;
            const coords = window.GeoPST_Geolocation.getPosition();
            if (coords) {
                map.getView().animate({ center: coords, zoom: 11, duration: 600 });
            }
        }
    });

    $(document).on('click', '.waypoint-action-btn.delete', function () {
        const index = $(this).closest('.routing-waypoint-item').data('index');
        removeWaypoint(index);
    });

    // Make whole waypoint item clickable for picking (Mobile UX)
    $(document).on('click', '.routing-waypoint-item', function (e) {
        // Don't trigger if clicked on action buttons
        if ($(e.target).closest('.waypoint-action-btn').length) return;
        const index = $(this).data('index');
        enableMapPicking(index);
    });
}

// ═══════════════════════════════════════════════════════════════
// WAYPOINT MANAGEMENT
// ═══════════════════════════════════════════════════════════════
function addWaypoint(type) {
    waypoints.push({
        coords: null,
        feature: null,
        label: '',
        type: type, // 'start', 'middle', 'end'
        isManual: false
    });
}

function removeWaypoint(index) {
    if (waypoints.length <= 2) return; // Keep at least start and end
    const wp = waypoints[index];
    if (wp.feature) routeSource.removeFeature(wp.feature);
    waypoints.splice(index, 1);
    renderWaypointsList();
    calculateAndDrawRoute();
    updateGoButtonState();
}

function setWaypointCoords(index, coords, isManual = null) {
    if (index < 0 || index >= waypoints.length) return;

    const wp = waypoints[index];
    if (isManual !== null) wp.isManual = isManual;
    wp.coords = coords;

    // Create label
    const proj = map.getView().getProjection();
    if (proj.getAxisOrientation() === 'neu') {
        wp.label = `${coords[1].toFixed(0)}, ${coords[0].toFixed(0)}`;
    } else {
        wp.label = `${coords[0].toFixed(0)}, ${coords[1].toFixed(0)}`;
    }

    // Remove old feature
    if (wp.feature) routeSource.removeFeature(wp.feature);

    // Create marker feature
    wp.feature = new Feature({
        geometry: new Point(coords),
        waypointIndex: index
    });

    let iconSrc = iconMiddle;
    if (wp.type === 'start') iconSrc = iconStart;
    if (wp.type === 'end') iconSrc = iconEnd;

    // [MOD] Hide start marker if it's based on GPS location (not manual)
    const shouldHide = (wp.type === 'start' && !wp.isManual);

    wp.feature.setStyle(new Style({
        image: shouldHide ? null : new Icon({ src: iconSrc, scale: 1, anchor: [0.5, 1] })
    }));

    routeSource.addFeature(wp.feature);
    renderWaypointsList();
    updateGoButtonState();

    // Auto-calculate route if we have at least start and end
    if (canCalculateRoute()) {
        calculateAndDrawRoute();
    }
}

function canCalculateRoute() {
    const filledWaypoints = waypoints.filter(w => w.coords);
    return filledWaypoints.length >= 2;
}

function clearAllWaypoints() {
    // Clear all features
    routeSource.clear();
    routeFeature = null;

    // Reset waypoints
    waypoints = [];
    addWaypoint('start');
    addWaypoint('end');

    // Clear UI
    renderWaypointsList();
    $('#routePreview').hide();
    updateGoButtonState();

    // End any navigation
    if (isNavigating) endNavigation();
}

// ═══════════════════════════════════════════════════════════════
// UI RENDERING
// ═══════════════════════════════════════════════════════════════
function renderWaypointsList() {
    const container = $('#waypointsList');
    container.empty();

    waypoints.forEach((wp, index) => {
        const typeClass = wp.type;
        let iconClass = 'fa-circle';
        let typeName = 'نقطة وسطى';

        if (wp.type === 'start') {
            iconClass = 'fa-play-circle';
            typeName = 'نقطة البداية';
        } else if (wp.type === 'end') {
            iconClass = 'fa-flag-checkered';
            typeName = 'نقطة الوصول';
        }

        const label = wp.coords ? wp.label : typeName;
        const isMiddle = wp.type === 'middle';

        container.append(`
            <div class="routing-waypoint-item ${typeClass}" data-index="${index}">
                <i class="waypoint-icon fas ${iconClass}"></i>
                <span class="waypoint-label">${label}</span>
                <div class="waypoint-actions">
                    <button class="waypoint-action-btn pick" title="اختر من الخريطة">
                        <i class="fas fa-map-marker-alt"></i>
                    </button>
                    ${isMiddle ? `
                    <button class="waypoint-action-btn delete" title="حذف">
                        <i class="fas fa-times"></i>
                    </button>
                    ` : ''}
                </div>
            </div>
        `);
    });
}

function updateGoButtonState() {
    const canGo = canCalculateRoute();
    $('#btnStartNavigation').prop('disabled', !canGo);
}

function updateRoutePreview(duration, distance) {
    const timeStr = formatTime(Math.round(duration / 60));
    const distStr = (distance / 1000).toFixed(1) + ' كم';

    $('#previewTime').text(timeStr);
    $('#previewDistance').text(distStr);
    $('#routePreview').show();
}

function formatTime(mins) {
    if (mins < 60) return `${mins} دقيقة`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h} ساعة ${m > 0 ? m + ' دقيقة' : ''}`;
}

// ═══════════════════════════════════════════════════════════════
// MAP PICKING
// ═══════════════════════════════════════════════════════════════
function enableMapPicking(forIndex) {
    disableMapPicking();

    // Hide sidebar on mobile to show full map for picking
    closeSidebarMobile();

    isPickingOnMap = true;
    pickingForIndex = forIndex;
    $('body').css('cursor', 'crosshair');
    $('#btnPickOnMap').addClass('active');

    clickListener = map.on('singleclick', (evt) => {
        setWaypointCoords(pickingForIndex, evt.coordinate, true); // Mark as MANUAL
        disableMapPicking();
    });
}

function disableMapPicking() {
    isPickingOnMap = false;
    pickingForIndex = -1;
    $('body').css('cursor', 'default');
    $('#btnPickOnMap').removeClass('active');
    if (clickListener) {
        unByKey(clickListener);
        clickListener = null;
    }
}

// ═══════════════════════════════════════════════════════════════
// GEOLOCATION HELPERS
// ═══════════════════════════════════════════════════════════════
function setStartFromMyLocation() {
    if (!window.GeoPST_Geolocation) {
        alert('نظام الموقع غير متوفر حالياً.');
        return;
    }

    const btn = $('#btnStartFromMyLocation');
    btn.html('<i class="fas fa-spinner fa-spin"></i> جاري التحديد...');
    btn.prop('disabled', true);

    // Start tracking if not active
    if (!window.GeoPST_Geolocation.isActive()) {
        window.GeoPST_Geolocation.start();
    }

    const checkPos = () => {
        const coords = window.GeoPST_Geolocation.getPosition();
        if (coords) {
            setWaypointCoords(0, coords, false); // Mark as NOT manual
            map.getView().animate({ center: coords, zoom: 11, duration: 500 });
            btn.html('<i class="fas fa-location-arrow"></i> ابدأ من موقعي');
            btn.prop('disabled', false);
            window.GeoPST_Geolocation.un('change:position', checkPos);
        }
    };

    const coords = window.GeoPST_Geolocation.getPosition();
    if (coords) {
        checkPos();
    } else {
        window.GeoPST_Geolocation.on('change:position', checkPos);
        // Timeout if no position found
        setTimeout(() => {
            if (btn.prop('disabled')) {
                alert('تعذر تحديد موقعك. يرجى التأكد من تفعيل الـ GPS.');
                btn.html('<i class="fas fa-location-arrow"></i> ابدأ من موقعي');
                btn.prop('disabled', false);
                window.GeoPST_Geolocation.un('change:position', checkPos);
            }
        }, 10000);
    }
}

// ═══════════════════════════════════════════════════════════════
// ROUTE CALCULATION
// ═══════════════════════════════════════════════════════════════
async function calculateAndDrawRoute() {
    const filledWaypoints = waypoints.filter(w => w.coords);
    if (filledWaypoints.length < 2) return;

    const mapProj = map.getView().getProjection();

    // Build OSRM coordinates string
    const coordsStr = filledWaypoints
        .map(w => {
            const lonLat = toLonLat(w.coords, mapProj);
            return `${lonLat[0]},${lonLat[1]}`;
        })
        .join(';');

    const url = `https://router.project-osrm.org/route/v1/driving/${coordsStr}?overview=full&geometries=geojson`;

    try {
        const response = await fetch(url);
        const data = await response.json();

        // ⚡ Guard: if navigation was ended while fetch was in-flight, abort
        if (!isNavigating && routeFeature === null && waypoints.every(w => !w.coords)) return;

        if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
            console.warn('OSRM: No route found');
            return;
        }

        const route = data.routes[0];

        // Remove old route
        if (routeFeature) routeSource.removeFeature(routeFeature);

        // Create new route geometry
        const geom = new LineString(route.geometry.coordinates);
        geom.transform('EPSG:4326', mapProj);

        routeFeature = new Feature({
            geometry: geom,
            type: 'route'
        });
        routeSource.addFeature(routeFeature);

        // Update preview
        updateRoutePreview(route.duration, route.distance);

        // Fit map to route if not navigating
        if (!isNavigating) {
            map.getView().fit(geom.getExtent(), {
                padding: [80, 80, 80, 80],
                duration: 800
            });
        }

    } catch (error) {
        console.error('Routing error:', error);
    }
}

// ═══════════════════════════════════════════════════════════════
// LIVE NAVIGATION
// ═══════════════════════════════════════════════════════════════
function startNavigation() {
    if (!canCalculateRoute()) return;
    if (!window.GeoPST_Geolocation) {
        alert('نظام الموقع غير متوفر.');
        return;
    }

    isNavigating = true;
    isFollowingNav = true; // Reset following on start

    // Show navigation bar
    $('#navigationStatsBar').fadeIn(300);
    document.body.classList.add('routing-active');

    // Hide sidebar on mobile for full map view
    closeSidebarMobile();
    initNavIdleTimer();

    // Start GPS tracking if not already active
    if (!window.GeoPST_Geolocation.isActive()) {
        window.GeoPST_Geolocation.start();
    }

    // Subscribe to unified geolocation updates
    window.GeoPST_Geolocation.on('change:position', syncNavigationWithGPS);

    // Immediate update if we have position
    const p = window.GeoPST_Geolocation.getPosition();
    if (p) syncNavigationWithGPS();
}

/**
 * Bridge function to sync Routing logic with Geolocation.js events
 */
function syncNavigationWithGPS() {
    if (!isNavigating || !window.GeoPST_Geolocation) return;
    const coords = window.GeoPST_Geolocation.getPosition();
    if (!coords) return;
    updateNavigationPosition(coords);
}

function updateNavigationPosition(mapCoords) {
    if (!isNavigating) return;

    // Update start point to current location (for route recalculation)
    setWaypointCoords(0, mapCoords, false); // Mark as NOT manual

    // 🗺️ Auto-rotate map to match device heading (Google Maps Style Navigation)
    let currentRotation = 0;
    if (isFollowingNav && window.GeoPST_Geolocation) {
        const heading = window.GeoPST_Geolocation.getHeading(); // in radians
        if (heading !== null && heading !== undefined) {
             currentRotation = -heading; // Map rotation is opposite to heading to keep UP as forward
        }
    }

    // Center map on current position only if following is active
    if (isFollowingNav) {
        map.getView().animate({
            center: mapCoords,
            zoom: 11,
            rotation: currentRotation,
            duration: 300
        });
    }

    // Recalculate route from current position
    recalculateNavigationStats(mapCoords);
}

async function recalculateNavigationStats(currentCoords) {
    if (!isNavigating) return; // ⚡ Guard: don't recalculate if navigation ended

    // 🛡️ API Throttling/Debouncing Logic
    const now = Date.now();
    let shouldRecalc = false;

    if (!lastRecalcCoords) {
        shouldRecalc = true;
    } else {
        const timePassed = now - lastRecalcTime;
        const dx = currentCoords[0] - lastRecalcCoords[0];
        const dy = currentCoords[1] - lastRecalcCoords[1];
        const distMoved = Math.sqrt(dx * dx + dy * dy);

        // Conditions for calling OSRM API:
        // 1. If user moved more than 20 meters (off-track)
        // 2. OR if 8 seconds passed since last update
        if (distMoved >= RECALC_DISTANCE_THRESHOLD || timePassed >= RECALC_THROTTLE_TIME) {
            shouldRecalc = true;
        }
    }

    if (!shouldRecalc) return;

    const filledWaypoints = waypoints.filter(w => w.coords);
    if (filledWaypoints.length < 2) return;

    const mapProj = map.getView().getProjection();

    // Build coordinates with current position as start
    const allCoords = [currentCoords, ...filledWaypoints.slice(1).map(w => w.coords)];
    const coordsStr = allCoords
        .map(c => {
            const lonLat = toLonLat(c, mapProj);
            return `${lonLat[0]},${lonLat[1]}`;
        })
        .join(';');

    const url = `https://router.project-osrm.org/route/v1/driving/${coordsStr}?overview=full&geometries=geojson`;

    try {
        lastRecalcTime = now;
        lastRecalcCoords = currentCoords;

        const response = await fetch(url);
        const data = await response.json();

        // ⚡ Guard: if navigation ended while fetch was in-flight, don't add route
        if (!isNavigating) return;

        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
            const route = data.routes[0];

            // Update floating stats bar
            $('#navTimeRemaining').text(formatTime(Math.round(route.duration / 60)));
            $('#navDistanceRemaining').text((route.distance / 1000).toFixed(1) + ' كم');

            // Update route line
            if (routeFeature) routeSource.removeFeature(routeFeature);
            const geom = new LineString(route.geometry.coordinates);
            geom.transform('EPSG:4326', mapProj);
            routeFeature = new Feature({ geometry: geom, type: 'route' });
            routeSource.addFeature(routeFeature);
        }
    } catch (error) {
        console.warn('Nav recalc error:', error);
    }
}

function endNavigation() {
    // Unsubscribe from unified geolocation updates
    if (window.GeoPST_Geolocation) {
        window.GeoPST_Geolocation.un('change:position', syncNavigationWithGPS);
    }

    // Mark navigation as ended — guards will prevent async routes from re-adding
    isNavigating = false;

    // Hide navigation bar
    $('#navigationStatsBar').fadeOut(300);

    // Clear route completely
    routeSource.clear();
    routeFeature = null;
    waypoints = [];
    lastRecalcCoords = null;
    lastRecalcTime = 0;
    addWaypoint('start');
    addWaypoint('end');
    renderWaypointsList();
    $('#routePreview').hide();
    updateGoButtonState();

    stopNavIdleTimer();
    document.body.classList.remove('routing-active');
}

// ═══════════════════════════════════════════════════════════════
// MOBILE UI HELPERS
// ═══════════════════════════════════════════════════════════════
function closeSidebarMobile() {
    closeSidebarOnMobile();
}

// ═══════════════════════════════════════════════════════════════
// IDLE TIMER (Mobile UI Optimization)
// ═══════════════════════════════════════════════════════════════
function initNavIdleTimer() {
    stopNavIdleTimer();

    const resetTimer = () => {
        $('#navigationStatsBar').removeClass('idle-faded');
        clearTimeout(navIdleTimer);
        navIdleTimer = setTimeout(() => {
            if (isNavigating) $('#navigationStatsBar').addClass('idle-faded');
        }, NAV_IDLE_TIME);
    };

    // Listen for interactions
    $(document).on('mousemove.navIdle click.navIdle touchstart.navIdle', resetTimer);
    resetTimer();
}

function stopNavIdleTimer() {
    clearTimeout(navIdleTimer);
    $(document).off('.navIdle');
    $('#navigationStatsBar').removeClass('idle-faded');
}

// ═══════════════════════════════════════════════════════════════
// INITIALIZE
// ═══════════════════════════════════════════════════════════════
init();

// ═══════════════════════════════════════════════════════════════
// GLOBAL API — Allow other modules to start routing to a coordinate
// ═══════════════════════════════════════════════════════════════
window.startRoutingTo = function (destinationCoord) {
    if (!window.GeoPST_Geolocation) {
        alert('نظام الموقع غير متوفر.');
        return;
    }

    // 1. Clear previous route
    clearAllWaypoints();

    // 2. Set destination (end waypoint = index 1)
    setWaypointCoords(1, destinationCoord);

    // 3. Get user location and auto-start navigation
    if (!window.GeoPST_Geolocation.isActive()) {
        window.GeoPST_Geolocation.start();
    }

    const startNav = () => {
        const coords = window.GeoPST_Geolocation.getPosition();
        if (coords) {
            setWaypointCoords(0, coords, false); // Mark as NOT manual
            setTimeout(() => {
                if (canCalculateRoute()) {
                    startNavigation();
                    isFollowingNav = false;
                }
            }, 800);
            window.GeoPST_Geolocation.un('change:position', startNav);
        }
    };

    const currentCoords = window.GeoPST_Geolocation.getPosition();
    if (currentCoords) {
        startNav();
    } else {
        window.GeoPST_Geolocation.on('change:position', startNav);
    }
};

// ✅ [INTERACTION VACUUM] - Lifecycle cleanup when switching tools
$(document).on('tool:cleanup', (e, data) => {
    // If we're picking coordinates and another tool starts, stop picking
    if (data.id !== 'btnOpenRouting' && isPickingOnMap) {
        disableMapPicking();
    }
});
